import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as ClaimTrackerModels from '@claim-tracker/common/models';
import isEmpty from 'lodash/isEmpty';
import { catchError, EMPTY, Observable } from 'rxjs';
import { sprintf } from 'sprintf-js';

import { PageLevelMessageService } from '../../shared/messages/page-level-message/page-level-message.service';
import { AppPropertiesService } from '../../shared/services/app-properties/app-properties.service';
import {
	DEFAULT_ERROR_MESSAGE,
	FIRST_PARTY_ERROR_MESSAGE,
	WRONG_EXPOSURE_ERROR_MESSAGE,
	WRONG_EXPOSURE_ERROR_MESSAGE_EXTRA_TEXT
} from '../data/login-errors';

/**
 * Service to attempt a user login.
 *
 * @author Jesse Bloomster e020902
 */
@Injectable()
export class LoginService {
	private readonly loginUrl = '/gateway/login';

	/**
	 * @param http the Angular HttpClient
	 * @param messageService the page level message service
	 * @param appPropertiesService the global app properties
	 */
	constructor(
		private http: HttpClient,
		private messageService: PageLevelMessageService,
		private appPropertiesService: AppPropertiesService
	) {}

	/**
	 * Attempt to log in the user.
	 *
	 * @param request login request
	 */
	login = (request: ClaimTrackerModels.com.aoins.claims.login.LoginRequest): Observable<any> => {
		const appProperties = this.appPropertiesService.appProperties;

		return this.http.post(this.loginUrl, request).pipe(
			catchError((err: unknown) => {
				let message: string;

				if (err instanceof HttpErrorResponse) {
					switch (err.error?.errorType) {
						case ClaimTrackerModels.com.aoins.claims.login.LoginErrorType.FIRST_PARTY_ERROR.name:
							message = sprintf(
								FIRST_PARTY_ERROR_MESSAGE,
								`<a href='${appProperties.customerCenterUrl}'>online access</a>`
							);
							break;
						case ClaimTrackerModels.com.aoins.claims.login.LoginErrorType.WRONG_EXPOSURE_ERROR.name: {
							const extraText = this.determineWrongExposureErrorExtraText(
								err.error?.errorDetails?.claimRep
							);
							message = `${WRONG_EXPOSURE_ERROR_MESSAGE}${extraText}`;
							break;
						}
						case ClaimTrackerModels.com.aoins.claims.login.LoginErrorType.DEFAULT_ERROR.name:
						default:
							message = DEFAULT_ERROR_MESSAGE;
							break;
					}
				} else {
					message = DEFAULT_ERROR_MESSAGE;
				}

				this.messageService.addPageLevelMessage({
					text: message,
					style: 'ao-page-error',
					isLoginFailureMessage: true
				});

				return EMPTY;
			})
		);
	};

	/**
	 * Determine the extra text needed for the wrong exposure error message. If the assigned claim rep and branch
	 * are both null, simply short circuit and return the empty string.
	 *
	 * @param claimRep the assigned claim rep or assigned claim branch
	 */
	private determineWrongExposureErrorExtraText(
		claimRep: ClaimTrackerModels.com.aoins.claims.claim.ClaimRep | null
	): string {
		// if the claim rep and branch are both nil, just return the empty string
		if (isEmpty(claimRep?.lastName) && isEmpty(claimRep?.claimBranch?.name)) {
			return '';
		}

		const contactName = !isEmpty(claimRep!.lastName)
			? `${claimRep!.nickName ?? claimRep!.firstName} ${claimRep!.lastName}`
			: `the ${claimRep!.claimBranch?.name ?? ''}`;

		const contactPhone = !isEmpty(claimRep!.lastName)
			? claimRep!.workPhoneNumber ?? ''
			: claimRep!.claimBranch?.tollFreePhoneNumber ?? '';

		return sprintf(WRONG_EXPOSURE_ERROR_MESSAGE_EXTRA_TEXT, contactName, contactPhone);
	}
}
